import { localISODate } from "@/lib/dates";

/**
 * The rules of the add/edit transaction form, kept apart from how it looks.
 *
 * The form edits either an ordinary transaction (one account, a category) or a
 * transfer (two accounts, no category). Both share the date, amount, note and
 * recurrence; `amount` is a number, or null while the field is empty.
 */

export const RECURRENCE_FREQUENCIES = ["weekly", "monthly", "quarterly", "yearly"];

export function newTransactionForm(accountId, today = localISODate()) {
  return {
    date: today,
    amount: null,
    type: "expense",
    category: "",
    description: "",
    recurring: false,
    recurrence_frequency: "monthly",
    account_id: accountId,
    from_account_id: accountId,
    to_account_id: "",
  };
}

export function transactionFormFrom(transaction) {
  const [from, to] = transaction.transfer_id
    ? transferEnds(transaction)
    : [transaction.account_id, ""];
  return {
    date: transaction.date,
    amount: transaction.amount,
    type: transaction.transfer_id ? "transfer" : transaction.type,
    category: transaction.category || "",
    description: transaction.description || "",
    recurring: Boolean(transaction.recurring),
    recurrence_frequency: transaction.recurrence_frequency || "monthly",
    account_id: transaction.account_id,
    from_account_id: from,
    to_account_id: to,
  };
}

// A transfer side knows its own account and the other side's; which is the
// source depends on whether this side is the outgoing expense.
function transferEnds(transaction) {
  return transaction.type === "expense"
    ? [transaction.account_id, transaction.transfer_account_id]
    : [transaction.transfer_account_id, transaction.account_id];
}

export const isTransferForm = (form) => form.type === "transfer";

/** What stops the form from being saved, as a message for the user, or null. */
export function transactionFormError(form) {
  if (!(form.amount > 0)) return "Enter an amount greater than 0";
  const error = isTransferForm(form) ? transferError(form) : categoryError(form);
  if (error) return error;
  if (!form.date) return "Select a date";
  return null;
}

function transferError(form) {
  if (!form.from_account_id || !form.to_account_id) return "Choose both accounts";
  if (form.from_account_id === form.to_account_id) return "Choose two different accounts";
  return null;
}

function categoryError(form) {
  return form.category ? null : "Select a category";
}

export function transactionPayload(form) {
  return {
    ...sharedPayload(form),
    type: form.type,
    category: form.category,
    account_id: form.account_id || null,
  };
}

export function transferPayload(form) {
  return {
    ...sharedPayload(form),
    from_account_id: form.from_account_id,
    to_account_id: form.to_account_id,
  };
}

function sharedPayload(form) {
  return {
    date: form.date,
    amount: form.amount,
    description: form.description,
    recurring: form.recurring,
    recurrence_frequency: form.recurring ? form.recurrence_frequency : null,
  };
}

/**
 * The types the form may switch between. A transfer is created as one, or
 * linked from two existing rows; an ordinary transaction does not turn into
 * one by switching the toggle, and a transfer needs two accounts to exist.
 */
export function availableTypes(editing, multipleAccounts) {
  if (editing?.transfer_id) return ["transfer"];
  const ordinary = ["expense", "income"];
  return multipleAccounts && !editing ? [...ordinary, "transfer"] : ordinary;
}
